import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetRemittanceDocumentsQuery,
  useUpdateRemittanceDocumentStatusMutation,
  useRequestAdditionalDocumentMutation,
  useGetAdditionalDocumentRequestsQuery,
  useGetRemittanceRequestableDocTypesQuery,
  useGetRemittanceDetailSummaryQuery,
} from "../../../../store/queries/remittance";

const useDocumentDetail = () => {
  const { remittanceID } = useParams();

  const [itemRejected, setItemRejected] = useState(false);
  const [note, setNote] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageData, setImageData] = useState("");
  const [docId, setDocId] = useState("");
  const [itemStatus, setItemStatus] = useState("");
  const [itemStatusLabel, setItemStatusLabel] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [canStatusChange, setCanStatusChange] = useState(false);
  const [docName, setDocName] = useState("");

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requests, setRequests] = useState([
    { comment: "", document_type: "" },
  ]);

  const {
    data: documentsData,
    isFetching,
    refetch,
  } = useGetRemittanceDocumentsQuery({
    remittance_id: remittanceID,
  });

  const { data: detailSummary } = useGetRemittanceDetailSummaryQuery({
    remittance_id: remittanceID,
  });

  const hasRequestPermission = detailSummary?.data?.permission?.find(
    (p) => p?.can_request_additional_document
  )?.can_request_additional_document ?? 0;

  const { data: additionalRequests } = useGetAdditionalDocumentRequestsQuery({
    remittance_id: remittanceID,
  });

  const { data: requestableDocTypes } =
    useGetRemittanceRequestableDocTypesQuery({
      remittance_id: remittanceID,
    });

  const [updateDocumentStatus] = useUpdateRemittanceDocumentStatusMutation();
  const [requestAdditionalDoc] = useRequestAdditionalDocumentMutation();

  const getDocType = (name) => {
    if (!name) return "";
    const ext = name.split(".").pop()?.toLowerCase();
    return ext === "pdf" ? "pdf" : "";
  };

  const handleViewImage = (item, documentTypeLabel) => {
    setItemRejected(false);
    setDocumentType(getDocType(item?.name));
    setDocId(item?.document_id);
    setRejectReason(item?.rejection_reason);
    setShowImageModal(true);
    setImageData(item?.url);
    setItemStatus(item?.status);
    setItemStatusLabel(item?.status_label);
    setCanStatusChange(item?.can_status_change);
    const ext = item?.name?.split(".")?.pop() || "pdf";
    setDocName(documentTypeLabel ? `${documentTypeLabel}.${ext}` : item?.name || "document");
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setImageData("");
    setDocId("");
    setDocumentType("");
    setDocName("");
  };

  const handleVerification = (id, status, rejectionNote) => {
    const body = {
      document_id: id,
      status: status,
    };
    if (status === 2 && rejectionNote) {
      body.rejection_reason = rejectionNote;
    }
    updateDocumentStatus(body)
      .unwrap()
      .then((result) => {
        if (result?.success) {
          toast.success(result?.message || "Document status updated");
        } else {
          toast.error(result?.message || "Failed to update status");
        }
        refetch();
        closeImageModal();
      })
      .catch(() => {
        toast.error("Failed to verify document");
      });
  };

  const handleDownloadPdf = async (url) => {
    setDownloadLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = docName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("PDF download failed:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleAddRequest = () => {
    setRequests((prev) => [...prev, { comment: "", document_type: "" }]);
  };

  const handleRemoveRequest = (index) => {
    setRequests((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRequestChange = (index, field, value) => {
    setRequests((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleCloseRequestModal = () => {
    setShowRequestModal(false);
    setRequests([{ comment: "", document_type: "" }]);
  };

  const handleRequestAdditional = () => {
    const hasEmpty = requests.some(
      (r) => !r.comment || !r.document_type
    );
    if (hasEmpty) {
      toast.error("Please fill all document type and comment fields");
      return;
    }

    const formData = new FormData();
    formData.append("remittance_id", remittanceID);
    requests.forEach((req, index) => {
      formData.append(`requests[${index}][comment]`, req.comment);
      formData.append(`requests[${index}][document_type]`, req.document_type);
    });

    requestAdditionalDoc(formData)
      .unwrap()
      .then((result) => {
        if (result?.success) {
          toast.success(result?.message || "Document requested successfully");
          handleCloseRequestModal();
        } else {
          toast.error(result?.message || "Failed to request document");
        }
      })
      .catch(() => {
        toast.error("Failed to request document");
      });
  };

  return {
    documentsData,
    isFetching,
    additionalRequests,
    requestableDocTypes,
    showImageModal,
    imageData,
    docId,
    itemStatus,
    itemStatusLabel,
    itemRejected,
    note,
    rejectReason,
    documentType,
    downloadLoading,
    canStatusChange,
    showRequestModal,
    requests,
    handleViewImage,
    closeImageModal,
    handleVerification,
    handleDownloadPdf,
    setItemRejected,
    setNote,
    setShowRequestModal,
    handleAddRequest,
    handleRemoveRequest,
    handleRequestChange,
    handleCloseRequestModal,
    handleRequestAdditional,
    hasRequestPermission,
  };
};

export default useDocumentDetail;
